export type Strategy = "asset" | "document" | "zenDocument" | "inertia" | "network";

interface HeadersLike {
  get(name: string): string | null;
}

interface RequestLike {
  method: string;
  mode: string;
  url: string;
  headers: HeadersLike;
}

export type PageProps = Record<string, unknown>;

// A task page the service worker can stand in for offline: `key` names its cache entry,
// `props` are what the page needs to render from the local store alone.
export interface InertiaRoute {
  component: string;
  key: string;
  props: PageProps;
}

export interface InertiaPage {
  component: string;
  props: PageProps;
  url?: string;
  version?: string | null;
  encryptHistory?: boolean;
}

type MaybeRoute = InertiaRoute | null;
type MaybePage = InertiaPage | null;
type MaybeString = string | null;

// The fetchedAt of a page the service worker made up: older than any real read, so
// whatever the device already holds for the page wins over its empty props.
export const OFFLINE_FETCHED_AT = "1970-01-01T00:00:00.000Z";

// A copy of this function lives in public/sw.js. Change it here — change it there too.
export function strategyFor(request: RequestLike, origin: string): Strategy {
  if (request.method !== "GET") return "network";

  // We compare the whole origin, not a string prefix: "https://a.example.com".startsWith
  // ("https://a.example") is true, so a prefix would let a spoofed subdomain suffix through.
  const url = new URL(request.url);
  if (url.origin !== origin) return "network";
  if (url.pathname.startsWith("/vite/")) return "asset";
  if (url.pathname.startsWith("/api/")) return "network";

  // Inertia goes over XHR (mode: cors|same-origin) to the same URLs as documents.
  // We cache real navigations only — otherwise JSON responses would land in the document cache.
  // Zen snapshots its queue at mount, so an external entry (the Android widget) must not
  // freeze a stale cached queue when a fresh one is available.
  if (request.mode === "navigate") return url.pathname === "/zen" ? "zenDocument" : "document";

  // A partial reload carries a slice of the props, not a page that could stand in for the route.
  if (request.headers.get("X-Inertia") !== "true") return "network";
  if (request.headers.get("X-Inertia-Partial-Component")) return "network";
  return inertiaRouteFor(request.url) ? "inertia" : "network";
}

// A copy of this function lives in public/sw.js. Change it here — change it there too.
export function shouldCacheDocument(response: ResponseLike): boolean {
  if (!response.ok) return false;
  // A response from a redirect chain must not go into the cache: served later for a
  // navigation it breaks the page — "Response served by service worker has redirected flag set".
  if (response.redirected) return false;
  return response.headers.get("X-Revolver-Cacheable") === "1";
}

// A copy of this function lives in public/sw.js. Change it here — change it there too.
export function assetHashes(html: string): string[] {
  const found = html.match(/\/vite\/assets\/[A-Za-z0-9._-]+/g) ?? [];
  return [...new Set(found)];
}

// A copy of this function lives in public/sw.js. Change it here — change it there too.
export function inertiaRouteFor(href: string): MaybeRoute {
  const url = new URL(href);
  const path = url.pathname;
  if (path === "/" || path === "/my-day") {
    return { component: "MyDay/Show", key: "/my-day", props: { pendingTasks: [], completedTasksExist: false, backlogNonEmpty: false } };
  }
  if (path === "/planned") return { component: "Planned/Show", key: path, props: { groups: [] } };
  if (path === "/templates") return { component: "Templates/Show", key: path, props: { templates: [] } };
  if (/^\/lists\/[^/]+$/.test(path)) {
    return { component: "Lists/Show", key: path, props: { pendingTasks: [], completedTasksExist: false } };
  }
  // The page derives its frame from the query itself.
  if (path === "/zen") return { component: "Zen/Show", key: path + url.search, props: {} };
  return null;
}

// A copy of this function lives in public/sw.js. Change it here — change it there too.
export function sharedProps(props: PageProps): PageProps {
  const shared: PageProps = {};
  for (const key of ["currentUser", "sidebar", "aiActions", "aiChatUrl", "discussPrompt", "discussPromptEnabled", "errors"]) {
    if (key in props) shared[key] = props[key];
  }
  return shared;
}

// A copy of this function lives in public/sw.js. Change it here — change it there too.
export function offlinePage(route: InertiaRoute, href: string, cached: MaybePage, latest: InertiaPage, version: MaybeString): InertiaPage {
  const url = new URL(href);
  const base: InertiaPage = cached ?? { component: route.component, props: { ...route.props, fetchedAt: OFFLINE_FETCHED_AT } };
  return {
    ...base,
    props: { ...base.props, ...sharedProps(latest.props) },
    url: url.pathname + url.search,
    version: version ?? latest.version ?? null,
    encryptHistory: latest.encryptHistory ?? false,
  };
}

// A copy of this function lives in public/sw.js. Change it here — change it there too.
export function pageFromHtml(html: string): MaybePage {
  const found = /data-page="([^"]*)"/.exec(html);
  if (!found) return null;
  const json = found[1]
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

interface ResponseLike {
  ok: boolean;
  redirected: boolean;
  headers: HeadersLike;
}
